import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { useCurrentUnit } from './useCurrentUnit.js'

// 7-day revenue forecast. Calls the forecast_next_7_days RPC and
// returns an array of {forecast_date, day_of_week, forecast_revenue,
// forecast_units, sample_size, confidence_pct}.
//
// The RPC reads item_sales which has tenant RLS, so we don't need to
// pass the actual restaurant_id — Postgres will filter to whatever
// the caller can see. We still need a UUID to satisfy the function
// signature, so we look one up first.
export function useSalesForecast() {
  const [forecast, setForecast] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Multi-unit scope — null for single-unit (first-active lookup kept).
  const { currentRestaurantId } = useCurrentUnit()

  async function load() {
    setLoading(true)
    setError(null)
    try {
      // The RPC filters internally by its p_restaurant_id arg. Grouped:
      // pass the selected unit directly so the forecast is scoped to it.
      // Single-unit (null): fall back to the first-active lookup — RLS
      // filters to tenants the user is a member of. Byte-identical.
      let restaurantId = currentRestaurantId
      if (!restaurantId) {
        const { data: rest, error: restErr } = await supabase
          .from('restaurants')
          .select('id')
          .eq('active', true)
          .order('created_at')
          .limit(1)
          .maybeSingle()
        if (restErr) throw restErr
        restaurantId = rest?.id ?? null
      }
      if (!restaurantId) {
        setForecast([])
        return
      }

      const { data, error: rpcErr } = await supabase.rpc('forecast_next_7_days', {
        p_restaurant_id: restaurantId,
      })
      if (rpcErr) throw rpcErr
      setForecast(data || [])
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useSalesForecast] load failed', e)
      setError(e?.message || 'Could not load forecast.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [currentRestaurantId])

  // Convenience derived values
  const totalForecast = forecast.reduce((sum, r) => sum + Number(r.forecast_revenue || 0), 0)
  const hasData = forecast.some((r) => Number(r.sample_size) >= 2)
  const avgConfidence = hasData
    ? Math.round(
        forecast
          .filter((r) => Number(r.sample_size) >= 2)
          .reduce((sum, r, _, arr) => sum + Number(r.confidence_pct) / arr.length, 0)
      )
    : 0

  return { forecast, totalForecast, avgConfidence, hasData, loading, error, refresh: load }
}
